import {
    getAxiosClient,
    type RefreshOutcome,
    refreshUserToken,
} from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { AuthEndpoints } from './authEndpoints';
import type { IAuthService } from './IAuthService';
import type { DeviceExchangeRequest, PinLoginRequest } from './requests';
import type {
    DeviceExchangeResponse,
    DeviceRosterResponse,
    PinLoginResponse,
    PinVerifyResponse,
} from './responses';

type AuthServiceOptions = {
    baseUrl: string;
};

/**
 * Transport for the POS sign-in flow. Which credential each call carries is
 * decided by the request interceptor (see `client/authInterceptors.ts`) from
 * the endpoint path — the service just names the endpoint and shape.
 */
export class AuthService implements IAuthService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: AuthServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public exchangeDevice(
        body: DeviceExchangeRequest,
    ): Promise<ApiResponse<DeviceExchangeResponse>> {
        const url = AuthEndpoints.deviceExchange();
        return makeApiRequest<DeviceExchangeResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
    }

    public getDeviceUsers(): Promise<ApiResponse<DeviceRosterResponse>> {
        const url = AuthEndpoints.deviceUsers();
        return makeApiRequest<DeviceRosterResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
    }

    public pinLogin(
        body: PinLoginRequest,
    ): Promise<ApiResponse<PinLoginResponse>> {
        const url = AuthEndpoints.pin();
        return makeApiRequest<PinLoginResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
    }

    public verifyPin(
        body: PinLoginRequest,
    ): Promise<ApiResponse<PinVerifyResponse>> {
        const url = AuthEndpoints.pinVerify();
        return makeApiRequest<PinVerifyResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
    }

    public logout(refreshToken: string): Promise<ApiResponse<void>> {
        const url = AuthEndpoints.logout();
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, {
                refreshToken,
            }),
        );
    }

    public refreshSession(): Promise<RefreshOutcome> {
        // The resolved `<baseUrl>/api/v1` the interceptor refreshes against.
        const { baseURL } = getAxiosClient({ baseURL: this.baseUrl }).defaults;
        return refreshUserToken(baseURL ?? '');
    }
}
